import React, {useEffect, useState} from "react"
import {Link, useParams} from "react-router-dom"
import {ColumnDef} from "@tanstack/react-table"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {Badge, Box, Tab, TabList, TabPanel, TabPanels, Tabs} from "@chakra-ui/react"

import {IGame, IGamePlay, IQuestion, IUser, QuestionField} from "@backend/types"
import {confidenceLevels} from "@backend/constants"
import {Card, Loading, Table} from "@components/index"
import {UserInfo} from "@modules/user/components"
import {GamePlayStatusBadge} from "@modules/gamePlay/components"

import {GameTitle} from "../../components"
import {gameApiCalls} from "../../service"
import {accessOptions} from "../../constants"


function Stat({title, content}) {

    return <div className="px-4 py-6 sm:px-6 lg:px-8">
        <p className="text-sm font-medium leading-6">{title}</p>
        <p className="mt-2 flex items-baseline gap-x-2">
         <span className="text-xl font-semibold tracking-tight ">
         {content}
          </span>
        </p>
    </div>
}

const playerColumns: ColumnDef<IGamePlay>[] = [
    {
        header: "Player",
        accessorKey: "userId",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({getValue}) => <UserInfo user={getValue() as IUser}/>
    },
    {
        header: "Play Info",
        accessorKey: "createdAt",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({row: {original}}) => (
            <div className="whitespace-nowrap text-gray-500 flex flex-col justify-start items-start space-y-1">
                <GamePlayStatusBadge status={original.completeStatus}/>
                <span>Started: {(new Date(original.createdAt)).toDateTime(true)}</span>
                <span>Last Play: {(new Date(original.updatedAt)).toDateTime(true)}</span>
            </div>)
    },
    {
        header: "Progress",
        accessorKey: "stats",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({row: {original}}) => (
            <div className="whitespace-nowrap text-gray-500 flex flex-col justify-start items-start space-y-1">
                <span>Questions Completed: {Object.keys(original.voteData || {}).length}</span>
                <span>Vote Count: {Object.values(original.voteData || {}).reduce((acc, vote) => {
                    acc += vote.length
                    return acc
                }, 0)}</span>
                <Link to={`/admin/gamePlays/${original._id}`} target="_blank" className="text-gray-900 underline">
                    Details
                </Link>
            </div>)
    }
]


const feedbackColumns: ColumnDef<any>[] = [
    {
        header: "Player",
        accessorKey: "userId",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({getValue}) => <UserInfo user={getValue() as IUser}/>
    },
    {
        header: "Question",
        accessorKey: "questionId",
        enableSorting: false,
        enableColumnFilter: false
    },
    {
        header: "Message",
        accessorKey: "message",
        enableSorting: false,
        enableColumnFilter: false
    }
]

type StatType = {
    game: IGame,
    gamePlays: IGamePlay[],
    questions: any,
    playerCount: number,
    gamesCompletedCount: number,
    feedbackCount: number,
    voteCount: number,
    feedbacks: {
        userId: any,
        questionId: any,
        message: string
    }[]
}


export default function GameStats() {
    const {gameId} = useParams()
    const [stats, setStats] = useState<StatType>()

    useEffect(() => {

        gameApiCalls.stats(gameId)
            .then(({game, gamePlays = []}) => {


                const {feedbackCount, voteCount} = gamePlays.reduce((acc, gamePlay) => {

                    acc.feedbackCount += (gamePlay.feedbacks || []).length
                    acc.voteCount += Object.values(gamePlay.voteData || {}).reduce((subAcc, vote) => {
                        subAcc += vote.length
                        return subAcc
                    }, 0)

                    return acc
                }, {feedbackCount: 0, voteCount: 0})

                const fields = game.modeOptions.fields.reduce((acc, field) => {
                    acc[field.optionKey] = field._id
                    return acc
                }, {})

                const questions = game.modeOptions.questionIds.reduce((acc, question: IQuestion) => {

                    acc[question._id] = question.texts.reduce((subAcc, text) => {
                        subAcc[fields[text.optionKey]] = text
                        return subAcc
                    }, {})

                    return acc
                }, {})


                for (const gamePlay of gamePlays) {
                    for (const qId of Object.keys(gamePlay.voteData || {})) {
                        const votes = gamePlay.voteData[qId]

                        for (const vote of votes) {

                            if (questions[qId] && questions[qId][vote.vote]) {
                                questions[qId][vote.vote][vote.confidenceLevel] = (questions[qId][vote.vote][vote.confidenceLevel] || 0) + 1
                            }
                        }
                    }


                }


                let stat: StatType = {
                    game,
                    gamePlays,
                    playerCount: gamePlays.length,
                    gamesCompletedCount: gamePlays.filter(gamePlay => gamePlay.completeStatus === "finished").length,
                    feedbackCount, voteCount,
                    questions,
                    feedbacks: gamePlays.reduce((acc, gamePlay) => {
                        acc.push(...(gamePlay.feedbacks || []).reduce((subAcc, feedback) => {
                            subAcc.push({
                                userId: gamePlay.userId,
                                questionId: questions[feedback.questionId as string][fields["input"]].text,
                                message: feedback.message
                            })
                            return subAcc
                        }, []))
                        return acc
                    }, [])
                }

                setStats(stat)

            })

    }, [])

    const questionColumnsHelper = (fields: QuestionField[]): ColumnDef<any>[] => [
        ...fields.map(field => ({
            header: field.isPinned ? "Input" : field.title || field.fileHeader,
            enableSorting: false,
            enableColumnFilter: false,
            cell: ({row: {original}}) => field.isPinned ? <span>{original[field._id].text}</span> :
                <div className="flex flex-col space-y-1 items-start whitespace-nowrap">
                    {Object.keys(confidenceLevels).filter(confidenceLevel => original[field._id][confidenceLevel])
                        .map(confidenceLevel => <Box
                            borderColor={confidenceLevels[confidenceLevel].colorCode}
                            color={confidenceLevels[confidenceLevel].colorCode}
                            className="border rounded flex justify-start items-center space-x-2 px-2">
                            <FontAwesomeIcon className="w-10 h-10" fixedWidth
                                             icon={["fal", confidenceLevels[confidenceLevel].icon]}/>
                            <span>{confidenceLevels[confidenceLevel].title}</span> : {original[field._id][confidenceLevel]}
                        </Box>)}
                </div>

        }))
    ]


    if (!stats)
        return <Loading/>

    const questionColumns = questionColumnsHelper(stats.game.modeOptions.fields || [])


    return <Card
        bodyClassName="p-0"
        title="Game Stats"
        actions={<GameTitle info={stats.game.info} extra={<Badge
            colorScheme="green">{accessOptions[stats.game.accessOptions.accessType].title}</Badge>}/>}
    >
        <div>
            <div className="grid grid-cols-2 lg:grid-cols-4">
                <Stat
                    title="Player Count"
                    content={stats.playerCount}
                />
                <Stat
                    title="Games Completed"
                    content={`${stats.gamesCompletedCount} / ${stats.playerCount}`}
                />

                <Stat
                    title="Votes"
                    content={stats.voteCount}
                />

                <Stat
                    title="Feedbacks"
                    content={stats.feedbackCount}
                />
            </div>

            <Tabs className="border-t" variant="enclosed-colored">
                <TabList>
                    <Tab>Players</Tab>
                    <Tab>Questions</Tab>
                    <Tab>Feedbacks</Tab>
                </TabList>

                <TabPanels>
                    <TabPanel p={0}>
                        <Table
                            columns={playerColumns}
                            data={stats.gamePlays}
                        />
                    </TabPanel>
                    <TabPanel p={0}>
                        <Table
                            columns={questionColumns}
                            data={Object.values(stats.questions)}
                        />
                    </TabPanel>
                    <TabPanel p={0}>
                        <Table
                            columns={feedbackColumns}
                            data={stats.feedbacks}
                        />
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </div>
    </Card>
}